/**
 * The graphics routes of the route table (`routes.ts` spreads these into `ROUTES`): the gfx designer's cloud library
 * (ACP-D45, GFX-D107; docs/gfx-m1/plan.md §3). Cloud only in M1, like the sites and alerts tables — no node serves
 * them (no `hosts`).
 *
 * Every permission is tenant-wide (`permissionScope: 'tenant'`): a graphics library belongs to the tenant, not to a
 * site or studio. `graphics.read` reads, `graphics.edit` creates, saves and deletes projects and themes and uploads
 * content, `graphics.publish` publishes (and retires) templates (M1 wave 4), `graphics.admin` sets the tenant's aspect
 * classes. The
 * staff tenant (D39) is refused every write (`staffTenant.ts`' `graphics` tag).
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  createGraphicsProjectRequestWireSchema,
  deleteGraphicsProjectResponseWireSchema,
  deleteGraphicsTemplateResponseWireSchema,
  deleteGraphicsThemeResponseWireSchema,
  graphicsAspectClassesWireSchema,
  graphicsProjectPathWireSchema,
  graphicsProjectResponseWireSchema,
  graphicsTemplatePathWireSchema,
  graphicsTemplateResponseWireSchema,
  graphicsTemplateVersionPathWireSchema,
  graphicsTemplateVersionResponseWireSchema,
  graphicsThemePathWireSchema,
  graphicsThemeResponseWireSchema,
  graphicsThemeVersionPathWireSchema,
  listGraphicsProjectsResponseWireSchema,
  listGraphicsTemplatesResponseWireSchema,
  listGraphicsThemesResponseWireSchema,
  publishGraphicsTemplateRequestWireSchema,
  publishGraphicsTemplateResponseWireSchema,
  requestGraphicsDownloadsRequestWireSchema,
  requestGraphicsDownloadsResponseWireSchema,
  requestGraphicsUploadsRequestWireSchema,
  requestGraphicsUploadsResponseWireSchema,
  saveGraphicsProjectRequestWireSchema,
  saveGraphicsThemeVersionRequestWireSchema,
  saveGraphicsThemeVersionResponseWireSchema,
  updateGraphicsAspectClassesRequestWireSchema,
} from './graphics.js';
import type { RouteDef } from './routes.js';

const MISSING_CONTENT =
  '`validation-failed` when a content object it names (a scene’s JSON or track, an asset) is not in the tenant’s content store — upload it first (`POST /graphics/content/uploads`); `issues` names each one.';

export const GRAPHICS_ROUTES = {
  listGraphicsProjects: {
    id: 'listGraphicsProjects',
    method: 'GET',
    path: '/graphics/projects',
    tag: 'graphics',
    summary: 'List graphics projects',
    description: 'The tenant’s graphics projects (the gfx designer’s cloud workspace), most recently updated first, as summaries; deleted ones are left out.',
    params: null,
    query: null,
    body: null,
    response: listGraphicsProjectsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  createGraphicsProject: {
    id: 'createGraphicsProject',
    method: 'POST',
    path: '/graphics/projects',
    tag: 'graphics',
    summary: 'Create a graphics project',
    description: `A new project at revision 1, its scenes and assets naming content objects already uploaded. \`projectId\` keeps the designer’s own id (moving a local project to the cloud) or makes a retry safe: an id the tenant already uses (deleted projects included) is \`conflict\`. ${MISSING_CONTENT}`,
    params: null,
    query: null,
    body: createGraphicsProjectRequestWireSchema,
    response: graphicsProjectResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  getGraphicsProject: {
    id: 'getGraphicsProject',
    method: 'GET',
    path: '/graphics/projects/{projectId}',
    tag: 'graphics',
    summary: 'Get a graphics project',
    description: 'The project, with a presigned GET (valid five minutes) for each content object its scenes name. Asset bytes are fetched through `POST /graphics/content/downloads` when the designer’s cache lacks them. `not-found` for a deleted project.',
    params: graphicsProjectPathWireSchema,
    query: null,
    body: null,
    response: graphicsProjectResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  saveGraphicsProject: {
    id: 'saveGraphicsProject',
    method: 'PUT',
    path: '/graphics/projects/{projectId}',
    tag: 'graphics',
    summary: 'Save a graphics project',
    description: `Replaces the project’s name, scenes and assets from the revision named (\`baseRevision\`), answering it at the next revision. Another save since is \`conflict\` (the designer offers overwrite or reload, D35); a deleted project is \`not-found\`. Each field is a per-field LWW register (D6), written only when it changed. ${MISSING_CONTENT}`,
    params: graphicsProjectPathWireSchema,
    query: null,
    body: saveGraphicsProjectRequestWireSchema,
    response: graphicsProjectResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteGraphicsProject: {
    id: 'deleteGraphicsProject',
    method: 'DELETE',
    path: '/graphics/projects/{projectId}',
    tag: 'graphics',
    summary: 'Delete a graphics project',
    description: 'Soft-deletes the project: it leaves the listing and reads `not-found`; its content objects stay (no content GC in M1). Its id stays taken.',
    params: graphicsProjectPathWireSchema,
    query: null,
    body: null,
    response: deleteGraphicsProjectResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  requestGraphicsUploads: {
    id: 'requestGraphicsUploads',
    method: 'POST',
    path: '/graphics/content/uploads',
    tag: 'graphics',
    summary: 'Upload content objects',
    description:
      'For each object (`sha256`, `size`, `contentType`; at most 256 MiB): whether the tenant already has it, else a presigned PUT under the tenant’s prefix, valid fifteen minutes, bound to its SHA-256 (S3 checks the signed `x-amz-checksum-sha256`) and its size (the signed `content-length`). Send the bytes with exactly the headers given. An object found in the store is confirmed (counted in the tenant’s stored bytes) here; an uploaded one when a save or a later upload request names it.',
    params: null,
    query: null,
    body: requestGraphicsUploadsRequestWireSchema,
    response: requestGraphicsUploadsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  requestGraphicsDownloads: {
    id: 'requestGraphicsDownloads',
    method: 'POST',
    path: '/graphics/content/downloads',
    tag: 'graphics',
    summary: 'Download content objects',
    description:
      'A presigned GET (valid five minutes) for each named object the tenant has confirmed — one a save named or an upload request found in its store — and the rest listed as `missing`. Never an object of another tenant: every key is under the caller’s own prefix. A POST because the list does not fit a query string.',
    params: null,
    query: null,
    body: requestGraphicsDownloadsRequestWireSchema,
    response: requestGraphicsDownloadsResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  getGraphicsAspectClasses: {
    id: 'getGraphicsAspectClasses',
    method: 'GET',
    path: '/graphics/settings/aspect-classes',
    tag: 'graphics',
    summary: 'Get the aspect-class set',
    description: 'The tenant’s aspect-class set (GFX-D100), copied into every new scene; `classes: null` and `version: 0` when it never saved one (the designer’s defaults apply).',
    params: null,
    query: null,
    body: null,
    response: graphicsAspectClassesWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  updateGraphicsAspectClasses: {
    id: 'updateGraphicsAspectClasses',
    method: 'PUT',
    path: '/graphics/settings/aspect-classes',
    tag: 'graphics',
    summary: 'Save the aspect-class set',
    description: 'Replaces the set from the `version` named (0: never saved); another save since is `conflict`. The cloud checks the shape and unique ids; the designer checks the set’s semantics before saving.',
    params: null,
    query: null,
    body: updateGraphicsAspectClassesRequestWireSchema,
    response: graphicsAspectClassesWireSchema,
    successStatus: 200,
    permission: 'graphics.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listGraphicsTemplates: {
    id: 'listGraphicsTemplates',
    method: 'GET',
    path: '/graphics/templates',
    tag: 'graphics',
    summary: 'List graphics templates',
    description: 'The tenant’s template library (M1 wave 4, GFX-D59: scenes published for fill-in), most recently published first; retired (deleted) templates are left out.',
    params: null,
    query: null,
    body: null,
    response: listGraphicsTemplatesResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  getGraphicsTemplate: {
    id: 'getGraphicsTemplate',
    method: 'GET',
    path: '/graphics/templates/{templateId}',
    tag: 'graphics',
    summary: 'Get a graphics template',
    description: 'The template and every version of it, newest first. `not-found` for a retired one (its versions stay readable by `GET …/versions/{version}`: a cue pins a version, D84).',
    params: graphicsTemplatePathWireSchema,
    query: null,
    body: null,
    response: graphicsTemplateResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  publishGraphicsTemplate: {
    id: 'publishGraphicsTemplate',
    method: 'POST',
    path: '/graphics/templates/{templateId}/versions',
    tag: 'graphics',
    summary: 'Publish a template version',
    description:
      'Publishes an uploaded `.gfxpkg` (one scene, gfx’s `packScene`) as the template’s next version — creating the template under `templateId` on its first publish — and answers it with its `template@sha256` reference. A version is immutable. The package must be a content object the tenant has (`validation-failed` naming `packageSha256` otherwise, or when its size is not the stored one); a retired template’s id is `conflict`.',
    params: graphicsTemplatePathWireSchema,
    query: null,
    body: publishGraphicsTemplateRequestWireSchema,
    response: publishGraphicsTemplateResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.publish',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  getGraphicsTemplateVersion: {
    id: 'getGraphicsTemplateVersion',
    method: 'GET',
    path: '/graphics/templates/{templateId}/versions/{version}',
    tag: 'graphics',
    summary: 'Get a template version',
    description: 'One published version and a presigned GET of its package (valid five minutes) — also of a retired template, since cues pin versions (D84).',
    params: graphicsTemplateVersionPathWireSchema,
    query: null,
    body: null,
    response: graphicsTemplateVersionResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  deleteGraphicsTemplate: {
    id: 'deleteGraphicsTemplate',
    method: 'DELETE',
    path: '/graphics/templates/{templateId}',
    tag: 'graphics',
    summary: 'Retire a graphics template',
    description: 'Soft-deletes the template: it leaves the library and reads `not-found`; its versions and packages stay readable by reference (cues pin them). Its id stays taken.',
    params: graphicsTemplatePathWireSchema,
    query: null,
    body: null,
    response: deleteGraphicsTemplateResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.publish',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  listGraphicsThemes: {
    id: 'listGraphicsThemes',
    method: 'GET',
    path: '/graphics/themes',
    tag: 'graphics',
    summary: 'List graphics themes',
    description: 'The tenant’s theme library (D84: theme packages, stacked at render time) with every version of every theme, by theme id. A version’s `theme.json` and assets are content objects (`POST /graphics/content/downloads`).',
    params: null,
    query: null,
    body: null,
    response: listGraphicsThemesResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  getGraphicsTheme: {
    id: 'getGraphicsTheme',
    method: 'GET',
    path: '/graphics/themes/{themeId}',
    tag: 'graphics',
    summary: 'Get a graphics theme',
    description: 'The theme with its versions and a presigned GET (valid five minutes) of each version’s `theme.json`.',
    params: graphicsThemePathWireSchema,
    query: null,
    body: null,
    response: graphicsThemeResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  saveGraphicsThemeVersion: {
    id: 'saveGraphicsThemeVersion',
    method: 'PUT',
    path: '/graphics/themes/{themeId}/versions/{version}',
    tag: 'graphics',
    summary: 'Save a theme version',
    description: `Adds a theme version (\`baseRevision: null\`; \`conflict\` when it exists) or replaces it from the revision named (another save since is \`conflict\`; a deleted version is \`not-found\`) — the designer’s theme library saving its working version in place. A theme keeps at most 98 versions (\`validation-failed\`). ${MISSING_CONTENT}`,
    params: graphicsThemeVersionPathWireSchema,
    query: null,
    body: saveGraphicsThemeVersionRequestWireSchema,
    response: saveGraphicsThemeVersionResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteGraphicsThemeVersion: {
    id: 'deleteGraphicsThemeVersion',
    method: 'DELETE',
    path: '/graphics/themes/{themeId}/versions/{version}',
    tag: 'graphics',
    summary: 'Delete a theme version',
    description: 'Removes one version (soft: its content stays); the theme goes when its last version does. The same `id@version` may be added again.',
    params: graphicsThemeVersionPathWireSchema,
    query: null,
    body: null,
    response: deleteGraphicsThemeResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  deleteGraphicsTheme: {
    id: 'deleteGraphicsTheme',
    method: 'DELETE',
    path: '/graphics/themes/{themeId}',
    tag: 'graphics',
    summary: 'Delete a graphics theme',
    description: 'Removes the theme and every version of it (soft: content stays). Its id may be used again.',
    params: graphicsThemePathWireSchema,
    query: null,
    body: null,
    response: deleteGraphicsThemeResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.edit',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Readonly<Record<string, RouteDef>>;
